import { evolutionValue } from './leagueEvolution';

// All views consume the same merged matrix and aggregation function. Slopes are
// unweighted OLS over comparison-season start years, never model parameters.
export function evolutionSeries(records, seasons, metric, mode) {
  return [...seasons].reverse().map(s => ({season: s.key, year: Number(s.key.slice(0,4)),
    ...evolutionValue(records, metric, mode, s.key)}));
}
function slope(points) {
  if (points.length < 2) return null;
  const x = points.reduce((n,p)=>n+p.year,0)/points.length;
  const y = points.reduce((n,p)=>n+p.value,0)/points.length;
  const denominator = points.reduce((n,p)=>n+(p.year-x)**2,0);
  return denominator ? points.reduce((n,p)=>n+(p.year-x)*(p.value-y),0)/denominator : null;
}
export function evolutionSummary(records, seasons, metric, mode) {
  const series = evolutionSeries(records, seasons, metric, mode);
  const valid = series.filter(p => p.value != null), latest = valid.at(-1) || null;
  const longTerm = evolutionValue(records, metric, mode);
  const windows = {};
  for (const n of [3,5,10]) {
    const points = latest ? series.filter(p=>p.year > latest.year-n && p.year<=latest.year) : [];
    const keys = new Set(points.map(p=>p.season));
    // Use exactly the same weighted matrix aggregation, not mean-of-means.
    const selected = records.filter(r => keys.has(r.comparison_season));
    windows[n] = {...evolutionValue(selected, metric, mode), available_seasons: points.filter(p=>p.value!=null).length,
      requested_seasons: n, slope: slope(points.filter(p=>p.value!=null))};
  }
  const min = valid.length ? Math.min(...valid.map(p=>p.value)) : null;
  const max = valid.length ? Math.max(...valid.map(p=>p.value)) : null;
  const previous = latest ? series.find(p=>p.year===latest.year-1) || null : null;
  return {series, long_term: longTerm, latest, previous, windows,
    delta_from_long_term: latest && longTerm.value!=null ? latest.value-longTerm.value : null,
    delta_from_last_5: latest && windows[5].value!=null ? latest.value-windows[5].value : null,
    minimum: min, minimum_seasons: valid.filter(p=>p.value===min).map(p=>p.season),
    maximum: max, maximum_seasons: valid.filter(p=>p.value===max).map(p=>p.season),
    latest_rank_descending: latest ? 1+valid.filter(p=>p.value>latest.value).length : null,
    available_seasons: valid.length, slope: slope(valid),
    jumps: valid.slice(1).flatMap((p,i)=>p.year===valid[i].year+1?[{season:p.season,delta:p.value-valid[i].value}]:[])};
}
export function evolutionAnalysisContract(matrix, metric, mode) {
  const strength = ['promoted','relegated'].includes(metric);
  const rows = matrix.rows.filter(row => !strength || row.records.some(r=>r.transitions?.[metric]));
  const pick = r => ({league_id:r.league_id, season_id:r.season_id, season_name:r.season_name,
    comparison_season:r.comparison_season, starting_at:r.starting_at, ending_at:r.ending_at,
    ...(strength ? {strength:r.transitions?.[metric] || null} : {
      actual:r.actual?.[metric] || null, expected:r.expected?.[metric] || null,
      comparison_actual:r.comparison_actual?.[metric] || null, comparison_kind:r.comparison_kind,
      expected_provenance:r.expected_provenance})});
  return {version:1, metric, sample_unit:strength?'teams':'fixtures', mode:strength?'stored_strength':mode,
    execution:{available:false, reason:'NO_CONNECTED_LLM_EXECUTOR'},
    instructions:{focus_only:metric, maximum_statements:5, minimum_statements:3,
      describe_not_evaluate:true, invent_numbers:false, invent_causes:false,
      distinguish_outlier_from_trend:true, missing_is_not_zero:true},
    method:{aggregation:'same weighted evolutionValue as matrix and chart',
      slope:'unweighted OLS of available season values on comparison-season start year; missing years omitted',
      windows:'calendar windows ending at each series latest available comparison season; available count explicit',
      strength:strength?'stored receiving-season total_strength; no claim of original initialization snapshot':null},
    collisions:matrix.collisions,
    leagues:rows.map(row=>({league:row.league, records:row.records.map(pick),
      analysis:evolutionSummary(row.records,matrix.seasons,metric,mode)})),
    global:evolutionSummary(rows.flatMap(r=>r.records),matrix.seasons,metric,mode)};
}
